import { QIcon_eye, QIcon_eye_closed, QIcon_pause_circle, QIcon_volumne } from '@repo/icons/index'
import { useContext, useEffect, useRef, useState } from 'react'
import { HACKLE_FLAG_KEY, useFeatureFlag } from '../../../../helpers/hackleHelper'
import { useConversationSetting } from '../../../../modules/apis/student/conversation'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import { n_ai_tutor } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { SoundContext, SoundProvider } from '../../../common/audio/play/Audio'
import Record from '../../../common/audio/record/Record'
import WhisperRecord from '../../../common/audio/record/WhisperRecord'
import { ActivityScreen, KeyboardModeBtn, KeyboardToolbar, Nudge, Word, judgeAnswer } from '../Common'

type RoleplayChat = {
  by: 'ai' | 'user' | 'context'
  content: string | Word[]
  content_kr: string
  content_ja: string
  // translated: string
  audio_url: string | Blob
  wordCount?: number
}
type RoleplayActivityProps = {
  roleplay: RoleplayChat[]
  done: boolean
  setDone: (done: boolean) => void
  nextActivity: () => void
  userRole: string
  aiRole: string
  addFail: () => void
  retry?: boolean
  idx?: any
}

const scrollToElement = (el: HTMLDivElement) => {
  setTimeout(() => {
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, 100)
}

const UserChatContent = ({ item, showKeywords, isKeyboardMode, onChangeValue, typedValue, inputRef }) => {
  const [isFocused, setIsFocused] = useState(false)
  const spanRef = useRef(null)
  const [inputWidth, setInputWidth] = useState(0)

  const onChangeTyping = (w: string) => {
    onChangeValue(w?.toLowerCase())
  }

  useEffect(() => {
    if (spanRef.current) {
      setInputWidth(spanRef.current.offsetWidth)
    }
  }, [item])

  return (
    <>
      {item.keyword ? (
        showKeywords ? (
          <span
            className={`bg-white rounded-lg border-solid border-gray-200 border-[1.5px] px-[8px] py-[2px] ${
              item.correct === true ? 'text-purple-500' : item.correct === false ? 'text-gray-700' : ''
            } ${item.ns ? 'mr-[4px]' : ''}`}
          >
            {item.word}
          </span>
        ) : (
          <span
            className={`bg-white rounded-lg border-solid border-gray-200 border-[1.5px] px-[8px] py-[2px] ${
              item.correct === true ? 'text-purple-500' : item.correct === false ? 'text-gray-700' : ''
            } ${item.ns ? 'mr-[4px]' : ''}  ${isFocused && isKeyboardMode ? 'border-gray-600' : ''} ${
              isKeyboardMode && 'text-purple-500'
            }`}
          >
            <span ref={spanRef} className={`absolute opacity-0 whitespace-pre Title1-20Bd pointer-events-none`}>
              {item.word}
            </span>
            <input
              type="text"
              ref={inputRef}
              value={typedValue}
              onChange={(e) => onChangeTyping(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              disabled={!isKeyboardMode}
              style={{
                all: 'unset',
                border: 'none',
                outline: 'none',
                width: inputWidth ? `${inputWidth + 4}px` : 'auto',
                background: !isKeyboardMode ? 'white' : 'none',
                opacity: !isKeyboardMode ? 0 : 1,
                whiteSpace: 'nowrap',
              }}
            />
          </span>
        )
      ) : (
        <span
          className={`${item.correct === true ? 'text-purple-500' : item.correct === false ? 'text-gray-700' : ''} ${
            item.ns ? 'mr-[4px]' : ''
          }`}
        >
          {item.word}
        </span>
      )}
    </>
  )
}

const renderContent = (
  content: string | Word[],
  showKeywords = true,
  active = showKeywords,
  isKeyboardMode = false,
  typedAnswers = null,
  setTypedAnswers = null,
  inputRefs = null
) => {
  const onChangeValue = (index: number, val: string) => {
    setTypedAnswers((prev) => ({ ...prev, [index]: val }))
  }

  if (Array.isArray(content)) {
    return (
      <div
        className={`flex flex-row gap-y-[4px] items-center justify-start flex-wrap content-center self-stretch shrink-0 relative ${
          active ? 'text-gray-700' : 'text-gray-400'
        } text-left Title2-18Bd align-middle`}
      >
        {content.map((item, index) => (
          <UserChatContent
            key={`user-chat-content-${index}`}
            item={item}
            isKeyboardMode={isKeyboardMode}
            showKeywords={showKeywords}
            onChangeValue={(val: string) => onChangeValue(index, val)}
            typedValue={typedAnswers?.[index] || ''}
            inputRef={(el: HTMLInputElement) => (inputRefs.current[index] = el)}
          />
        ))}
      </div>
    )
  } else {
    return <div className="text-gray-700 text-left Title2-18Bd relative self-stretch">{content}</div>
  }
}

const AIChat = ({ content, content_kr, content_ja, audio_url, aiRole, currPlay, isRecording }) => {
  const [playing, setPlaying] = useState(false)
  const { currentUser } = useCurrentUser()
  const { locale } = useTranslation()

  const playAudio = () => {
    if (isRecording) return
    if (!audio_url) return
    setPlaying(true)
    sound.stop()
    sound.play([audio_url]).then(() => setPlaying(false))
  }

  const stopAudio = () => {
    setPlaying(false)
    sound.stop()
  }

  const sound = useContext(SoundContext)
  return (
    <div className="bg-white rounded-xl border-solid border-gray-200 border p-[12px] flex flex-col gap-[8px] items-start justify-start flex-1 relative mr-[40px]">
      <div className="text-gray-500 text-left Body2-14Md relative self-stretch">{aiRole}</div>
      {renderContent(content)}
      {locale === 'ko' && <div className="text-gray-500 text-left Body2-14Md relative self-stretch">{content_kr}</div>}
      {locale === 'ja' && <div className="text-gray-500 text-left Body2-14Md relative self-stretch">{content_ja}</div>}
      <div className="flex flex-col gap-[4px] items-start justify-start self-stretch shrink-0 relative">
        <div className="bg-gray-200 self-stretch shrink-0 h-[1px] relative"></div>
      </div>
      <div className="flex flex-row items-center justify-between self-stretch shrink-0 relative">
        <div className="flex flex-row gap-[6px] items-start justify-start shrink-0 relative">
          {playing || currPlay ? (
            <QIcon_pause_circle width={20} height={20} onClick={stopAudio} />
          ) : (
            <QIcon_volumne width={20} height={20} onClick={playAudio} />
          )}
        </div>
      </div>
    </div>
  )
}

const UserChat = ({
  index,
  content,
  audio_url,
  content_kr,
  content_ja,
  userRole,
  openKeywords,
  isKeyboardMode,
  typedAnswers,
  setTypedAnswers,
}) => {
  const [showKeywords, setShowKeywords] = useState(false)
  const sound = useContext(SoundContext)
  const [playing, setPlaying] = useState(false)
  // const { currentUser } = useCurrentUser()
  const { locale } = useTranslation()

  const inputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({})

  const playAudio = () => {
    sound.play([audio_url]).then(() => setPlaying(false))
    setPlaying(true)
  }

  const stopAudio = () => {
    sound.stop()
    setPlaying(false)
  }

  useEffect(() => {
    if (isKeyboardMode && Array.isArray(content)) {
      const firstHiddenIndex = content?.findIndex((w: Word) => w.keyword)
      if (firstHiddenIndex >= 0) {
        inputRefs.current[firstHiddenIndex]?.focus()
      }
    }
  }, [isKeyboardMode, content])

  return (
    <div className="bg-gray-100 rounded-xl border-solid border-gray-200 border p-[12px] flex flex-col gap-[8px] items-end justify-start self-stretch shrink-0 relative ml-[40px]">
      <div className="text-gray-500 text-left text-[14px] leading-[20px] font-medium relative self-stretch">
        {userRole}
      </div>
      <div className="flex flex-col gap-[8px] items-start justify-start self-stretch shrink-0 relative">
        {renderContent(
          content,
          showKeywords || openKeywords,
          openKeywords,
          isKeyboardMode,
          typedAnswers,
          setTypedAnswers,
          inputRefs
        )}
      </div>
      {locale === 'ko' && (
        <div className="text-gray-500 text-left text-[14px] leading-[20px] font-medium relative self-stretch">
          {content_kr}
        </div>
      )}
      {locale == 'ja' && (
        <div className="text-gray-500 text-left text-[14px] leading-[20px] font-medium relative self-stretch">
          {content_ja}
        </div>
      )}
      <div className="flex flex-col gap-[4px] items-start justify-start self-stretch shrink-0 relative">
        <div className="bg-gray-200 self-stretch shrink-0 h-[1px] relative"></div>
      </div>
      <div className="flex flex-row items-center justify-between self-stretch shrink-0 relative">
        {audio_url ? (
          playing ? (
            <QIcon_pause_circle width={20} height={20} onClick={stopAudio} />
          ) : (
            <QIcon_volumne width={20} height={20} onClick={playAudio} />
          )
        ) : showKeywords ? (
          <QIcon_eye_closed
            className="shrink-0 w-[20px] h-[20px] relative overflow-visible cursor-pointer"
            onClick={() => setShowKeywords(!showKeywords)}
          />
        ) : (
          <QIcon_eye
            className="shrink-0 w-[20px] h-[20px] relative overflow-visible cursor-pointer"
            onClick={() => setShowKeywords(!showKeywords)}
          />
        )}
      </div>
    </div>
  )
}

const RoleplayActivityInner = ({
  roleplay,
  done,
  setDone,
  nextActivity,
  userRole,
  aiRole,
  addFail,
  retry,
  idx,
}: RoleplayActivityProps) => {
  const { t, locale } = useTranslation()
  const [content, setContent] = useState([] as RoleplayChat[])
  const [openKeywords, setOpenKeywords] = useState(-1)
  const [solved, setSolved] = useState<null | 'done' | 'retry-audio' | 'fail' | 'fail-audio'>(null)
  const messageEnd = useRef<HTMLDivElement>()
  const scrollTo = (e = null) => scrollToElement(e || messageEnd.current)
  const sound = useContext(SoundContext)
  const [currPlay, setCurrPlay] = useState(null)
  const [wordCount, setWordCount] = useState(0)
  const setting = useConversationSetting()
  const [isRecording, setIsRecording] = useState<boolean>(false)

  const [isKeyboardMode, setIsKeyboardMode] = useState(false)
  const [typedAnswers, setTypedAnswers] = useState<Record<number, string>>({})
  const [waitingTranscription, setWaitingTranscription] = useState(false)
  const isWhisperSTTOn = useFeatureFlag(HACKLE_FLAG_KEY.AI_TUTOR_STT_NEXT)

  useEffect(() => {
    sound.speed = setting.data?.speed || 1
  }, [setting])

  useEffect(() => {
    const li = []
    let playPr: Promise<any> = sound.stop()
    for (let i = 0; i < roleplay.length; i++) {
      const msg = roleplay[i]
      li.push(msg)
      setWordCount(msg.wordCount || 0)
      if (msg.by === 'user') {
        if ((navigator as any).audioSession) (navigator as any).audioSession.type = 'play-and-record'
        break
      } else {
        if ((navigator as any).audioSession) (navigator as any).audioSession.type = 'playback'
        playPr = playPr.then(() => {
          setCurrPlay(i)
          return sound.play([msg.audio_url])
        })
      }
    }
    playPr.then(() => setCurrPlay(null))
    setContent(li)
  }, [roleplay])

  const handleStopRecording = (text: string, audio: Uint8Array) => {
    const c = content.length - 1
    content[c].content = judgeAnswer(content[c].content as any, text).result
    content[c].audio_url = new Blob([audio], { type: 'audio/wav' })
    for (let i = c + 1; i < roleplay.length; i++) {
      content.push(roleplay[i])
      setWordCount(roleplay[i].wordCount || 0)
      if (roleplay[i].by === 'user') {
        if ((navigator as any).audioSession) (navigator as any).audioSession.type = 'play-and-record'
        break
      } else {
        if ((navigator as any).audioSession) (navigator as any).audioSession.type = 'playback'
        sound.play([roleplay[i].audio_url]).then(() => setCurrPlay(null))
        setCurrPlay(i)
      }
    }
    setContent(content)
    setOpenKeywords(c)
    scrollTo()
  }

  const onClickSubmit = () => {
    // typedAnswers
    const c = content.length - 1
    let sentence = ''
    if (Array.isArray(content[c].content)) {
      ;(content[c].content as Word[]).map((item, index) => {
        if (item.keyword) {
          sentence += typedAnswers[index] + ' '
        } else {
          sentence += item.word + ' '
        }
      })
    }

    console.log(sentence)

    content[c].content = judgeAnswer(content[c].content as any, sentence).result
    content[c].audio_url = roleplay[c].audio_url

    for (let i = c + 1; i < roleplay.length; i++) {
      content.push(roleplay[i])
      setWordCount(roleplay[i].wordCount || 0)
      if (roleplay[i].by === 'user') {
        if ((navigator as any).audioSession) (navigator as any).audioSession.type = 'play-and-record'
        break
      } else {
        if ((navigator as any).audioSession) (navigator as any).audioSession.type = 'playback'
        sound.play([roleplay[i].audio_url]).then(() => setCurrPlay(null))
        setCurrPlay(i)
      }
    }
    setContent(content)
    setOpenKeywords(c)
    scrollTo()
    setTypedAnswers(null)
    // setIsKeyboardMode(false)
  }

  const onClickKeyboardMode = () => {
    setIsKeyboardMode(true)
  }

  useEffect(() => {
    if (
      (openKeywords === content.length - 1 && content.length) ||
      (roleplay.length === content.length && content[content.length - 1].by !== 'user' && content.length)
    ) {
      setSolved('done')
      setDone(true)
      setTimeout(() => scrollTo(), 300)
    }
  }, [content, openKeywords, roleplay])

  return (
    <ActivityScreen
      title={t(n_ai_tutor.study_activity.six_title)}
      solved={solved}
      nextActivity={nextActivity}
      idx={idx}
    >
      <div className={`flex flex-col gap-[16px] flex-1 w-full overflow-y-auto ${solved && 'pb-[102px]'}`}>
        {content.map(({ by, content, content_kr, content_ja, audio_url }, index) => (
          <div key={index}>
            {by === 'context' && (
              <Nudge className="Body2-16Md" title={t(n_ai_tutor.study_activity.label3)}>
                {locale === 'ko' ? content_kr : locale == 'ja' ? content_ja || content : content}
              </Nudge>
            )}
            {by === 'ai' && (
              <AIChat
                content={content}
                content_kr={content_kr}
                content_ja={content_ja}
                audio_url={audio_url}
                aiRole={aiRole}
                currPlay={index === currPlay}
                isRecording={isRecording}
              />
            )}
            {by === 'user' && (
              <UserChat
                index={index}
                content={content}
                audio_url={audio_url}
                content_kr={content_kr}
                content_ja={content_ja}
                userRole={userRole}
                openKeywords={openKeywords >= index}
                isKeyboardMode={isKeyboardMode}
                typedAnswers={typedAnswers}
                setTypedAnswers={setTypedAnswers}
              />
            )}
          </div>
        ))}
        {isKeyboardMode && <div className="mb-[72px]" />}
        <div ref={messageEnd}></div>
      </div>
      {!solved && !isKeyboardMode && (
        <>
          {isWhisperSTTOn ? (
            <WhisperRecord
              handleStopRecording={handleStopRecording}
              setIsRecording={setIsRecording}
              isLoading={false}
              helper={t(n_ai_tutor.study_activity.detail1)}
              beforeRecord={() => {
                sound.stop()
              }}
              noMargin={true}
              waitingTranscription={waitingTranscription}
              setWaitingTranscription={setWaitingTranscription}
            />
          ) : (
            <Record
              handleStopRecording={handleStopRecording}
              beforeRecord={() => sound.stop()}
              helper={t(n_ai_tutor.study_activity.detail2)}
              onStartRecord={() => {
                setIsRecording(true)
              }}
              onFinishRecord={() => {
                setIsRecording(false)
              }}
              noMargin
              wordCount={wordCount}
            />
          )}
        </>
      )}
      {!isRecording && !waitingTranscription && <KeyboardModeBtn onClickKeyboardMode={onClickKeyboardMode} />}
      {isKeyboardMode && <KeyboardToolbar setIsKeyboardMode={setIsKeyboardMode} onClickSubmit={onClickSubmit} />}
    </ActivityScreen>
  )
}

export const RoleplayActivity = (props: RoleplayActivityProps) => (
  <SoundProvider key={'roleplay-activity'}>
    <RoleplayActivityInner {...props} />
  </SoundProvider>
)
